import { useLocale } from "@hooks";
import { color, ink } from "@styles";
import ArrowIcon from "components/atoms/ArrowIcon";
import { mobile } from "components/templates/Breakpoints";
import router from "next/router";
import { Category } from "pages/resource-hub";
import { FC, useMemo, useState } from "react";
import styled from "styled-components";

interface SelectInputProps {
  options: Option[];
  value: string;
  handleSelectChange: (slug: string, path: string) => void;
  path: string;
}

interface Option {
  slug?: string;
  name?: string;
}

const SelectInput: FC<SelectInputProps> = ({ options, value, handleSelectChange, path }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { locale } = useLocale();

  const chooseCategoryTranslation = useMemo(() => {
    const localeTexts: Record<string, string> = {
      jp: "ページを選択",
      default: "Choose a category",
    };

    return localeTexts[locale] || localeTexts.default;
  }, [locale]);

  const handlePrefetch = (optionSlug: string) => {
    router.prefetch(`/${path}/category/${optionSlug}/`);
  };

  return (
    <Select isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
      <Container>
        <Header isOpen={isOpen}>
          {value
            ? options.find((option: Category) => option.slug === value)?.name
            : chooseCategoryTranslation}
        </Header>
        <ArrowIcon rotate={isOpen} color={isOpen ? ink.inkBase : color.white} />
      </Container>
      <DropdownList isOpen={isOpen}>
        {options.map((option: Category) => (
          <DropdownItem
            onClick={() => handleSelectChange(option.slug, path)}
            onPointerEnter={() => handlePrefetch(option.slug)}
            key={option.slug}
          >
            {option.name}
          </DropdownItem>
        ))}
      </DropdownList>
    </Select>
  );
};

export default SelectInput;

const Select = styled.div<{ isOpen: boolean }>`
  width: 17.75rem;
  height: 4rem;
  border-radius: ${(p) => (p.isOpen ? "0.5rem 0.5rem 0 0" : "0.5rem")};
  border: ${(p) => (p.isOpen ? `2px solid ${color.B240}` : `2px solid rgb(199, 180, 253)`)};
  cursor: pointer;
  background: ${(p) => (p.isOpen ? color.white : "none")};

  ${mobile} {
    height: 3.5rem;
    width: 100%;
  }
`;

const Header = styled.p<{ isOpen: boolean }>`
  color: ${(p) => (p.isOpen ? ink.inkBase : color.white)};
  letter-spacing: 0.075rem;
  line-height: 0;
`;

export const DropdownList = styled.div<{ isOpen: boolean }>`
  position: relative;
  top: 1px;
  left: -2px;
  max-height: 24rem;
  overflow-y: auto;
  background: ${color.white};
  width: 17.75rem;
  border-radius: 0.5rem;
  border: 2px solid ${ink.inkNonEssential};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: 0;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};

  ${mobile} {
    width: calc(100% + 4px);
  }
  z-index: 1;
`;

export const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  background: ${color.white};
  color: ${ink.inkBase};

  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;

  &:hover {
    background: ${color.S310};
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 1.5rem;
`;
